import React, { useEffect, useState, useRef } from 'react';
import { getQuestionsPaged } from '../../services/question';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa';
import { FiSearch } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from 'src/Loading/LoadingScreen';
import './style.css';

interface Question {
  id?: string;
  en_questionText: string;
  ar_questionText?: string;
  type: string;
  value: number;
  isMandatory: boolean;
  questionCategoryId: string;
  validation?: { min?: unknown; max?: unknown };
  options?: { en_value: string }[];
  index?: number;
}

export default function ViewQuestionsTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [globalLoading, setGlobalLoading] = useState<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const didSkipFirstSearch = useRef<boolean>(false);

  const fetchPage = async (query: string = '', pageNo: number = 1, mode: 'global' | 'table' = 'table') => {
    mode === 'global' ? setGlobalLoading(true) : setTableLoading(true);
    try {
      const apiPageNo = Math.max(pageNo - 1, 0);
      const result = await getQuestionsPaged(query, pageSize, apiPageNo);
      setQuestions(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
    } finally {
      mode === 'global' ? setGlobalLoading(false) : setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchPage('', 1, 'global');
    didSkipFirstSearch.current = true;
  }, []);

  useEffect(() => {
    if (didSkipFirstSearch.current) {
      didSkipFirstSearch.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const q = (searchTerm || '').trim();
      setPage(1);
      fetchPage(q, 1, 'table');
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  if (globalLoading) {
    return (
      <div className="loading-screen" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
        <div>
          <LoadingScreen />
        </div>
      </div>
    );
  }

  const totalLabel = Math.max(totalPages || 1, 1);
  const searchQuery = (searchTerm || '').trim();

  const rowIndex = (index: number, q: Question) =>
    q.index != null ? q.index : (page - 1) * pageSize + index + 1;

  return (
    <div className="root qa-view-page">
      <header className="qa-view-header">
        <h1 className="qa-view-title">{t('question.Question Assessment')}</h1>
        <p className="qa-view-subtitle">{t('question.view_questions_subtitle')}</p>
      </header>

      <div className="qa-view-control-card">
        <div className="qa-view-toolbar">
          <div className="qa-view-search-wrap">
            <FiSearch className="qa-view-search-icon" size={18} aria-hidden />
            <input
              type="search"
              className="qa-view-search-input"
              placeholder={String(t('header.Search ....'))}
              aria-label={String(t('header.Search ....'))}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setPage(1);
                  fetchPage(searchQuery, 1, 'table');
                }
              }}
            />
          </div>
          <button
            type="button"
            className="qa-view-btn qa-view-btn-add"
            onClick={() => navigate('/ctreatQuestionAssessment')}
          >
            <FaPlus size={16} aria-hidden />
            <span>{t('question.Add Question')}</span>
          </button>
        </div>
      </div>

      <div className="qa-view-table-panel">
        <div className="table-responsive">
          <table className="qa-view-table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">{t('question.English Question')}</th>
                <th scope="col">{t('question.Arabic Question')}</th>
                <th scope="col">{t('question.Type')}</th>
                <th scope="col">{t('question.Mandatory')}</th>
                <th scope="col">{t('question.Validation')}</th>
                <th scope="col">{t('question.Options')}</th>
              </tr>
            </thead>
            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan={7}>
                    <div className="qa-view-table-loading">
                      <div className="spinner-border qa-view-spinner" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : questions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="qa-view-empty-cell">
                    {t('question.no_questions')}
                  </td>
                </tr>
              ) : (
                questions.map((q, index) => (
                  <tr key={q.id || index}>
                    <td className="qa-view-td-index">{rowIndex(index, q)}</td>
                    <td className="qa-view-td-text">{q.en_questionText}</td>
                    <td className="qa-view-td-text" dir="auto">
                      {q.ar_questionText || '—'}
                    </td>
                    <td>
                      <span className="qa-view-type-pill">{q.type}</span>
                    </td>
                    <td>{q.isMandatory ? t('question.yes_short') : t('question.no_short')}</td>
                    <td className="qa-view-td-muted">
                      {q.validation
                        ? `${t('question.min_label')}: ${q.validation.min ?? '—'} | ${t('question.max_label')}: ${
                            q.validation.max ?? '—'
                          }`
                        : '—'}
                    </td>
                    <td className="qa-view-td-options">
                      {q.options && q.options.length > 0 ? (
                        <ul className="qa-view-options-list">
                          {q.options.map((opt, i) => (
                            <li key={i}>{opt.en_value}</li>
                          ))}
                        </ul>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <nav className="qa-view-pagination" aria-label={String(t('surveys.page'))}>
        <button
          type="button"
          className="qa-view-page-btn"
          onClick={() => {
            setPage(1);
            fetchPage(searchQuery, 1, 'table');
          }}
          disabled={page === 1}
        >
          «
        </button>
        <button
          type="button"
          className="qa-view-page-btn"
          onClick={() => {
            const p = Math.max(page - 1, 1);
            setPage(p);
            fetchPage(searchQuery, p, 'table');
          }}
          disabled={page === 1}
        >
          ‹ {t('institution.pagination_previous')}
        </button>
        <span className="qa-view-page-current" aria-current="page">
          {page}
        </span>
        <button
          type="button"
          className="qa-view-page-btn"
          onClick={() => {
            const p = Math.min(page + 1, totalLabel);
            setPage(p);
            fetchPage(searchQuery, p, 'table');
          }}
          disabled={page >= totalLabel}
        >
          {t('institution.pagination_next')} ›
        </button>
        <button
          type="button"
          className="qa-view-page-btn"
          onClick={() => {
            setPage(totalLabel);
            fetchPage(searchQuery, totalLabel, 'table');
          }}
          disabled={page >= totalLabel}
        >
          »
        </button>
        <span className="qa-view-page-meta">
          {t('surveys.page')} {page} {t('surveys.to')} {totalLabel}
        </span>
      </nav>
    </div>
  );
}
